import {
	AfterContentChecked,
	AfterContentInit,
	ChangeDetectionStrategy,
	Component,
	ContentChild,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	TemplateRef,
	ViewEncapsulation,
} from "@angular/core";

import { CollectionPaging } from "@intocare/datatypes/lib/bundle";

import { SortDirection } from "../enums";

interface TableCellTemplate {
	header?: TemplateRef<any>;
	cell?: TemplateRef<any>;
	footer?: TemplateRef<any>;
}

@Component({
	selector: "ic-table",
	templateUrl: "./table.component.html",
	styleUrls: ["./table.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class TableComponent implements OnChanges, AfterContentInit, AfterContentChecked {
	selectTemplate: TableCellTemplate;
	actionsTemplate: TableCellTemplate;
	iconTemplate: TableCellTemplate;
	expandTemplate: TableCellTemplate;
	detailRowTemplate: TableCellTemplate;
	borderIndicationTemplate: TableCellTemplate;
	hasFooter = false;
	tableItems: any[] = [];
	tableColumns: string[] = [];
	templateMap = new Map<string, TableCellTemplate>();
	columns: string[] = [];

	@Input() items: any[] = [];
	@Input() visible: string[] = [];
	@Input() pagingParams: CollectionPaging;
	@Input() sortMap: { [key: string]: (a: unknown, b: unknown) => number };
	@Input() stickyFooter: boolean;
	@Input() fixedLayout: boolean;

	@Output() rowClick = new EventEmitter<any>();
	@Output() expandClick = new EventEmitter<any>();

	@ContentChild("emptyStateTmpl", { static: false }) emptyStateTemplate: TemplateRef<any>;
	@ContentChild("loadingTmpl", { static: false }) loadingTemplate: TemplateRef<any>;
	@ContentChild("errorTmpl", { static: false }) errorTemplate: TemplateRef<any>;

	@HostBinding("class.is-empty") empty = true;

	@HostBinding("class.is-loading") @Input() loading = false;
	@HostBinding("class.is-error") @Input() error = false;

	@HostBinding("class.has-buttons") @ContentChild("buttonTmpl", { static: false }) buttonTemplate: TemplateRef<any>;
	@HostBinding("class.has-buttons") @ContentChild("pagingTmpl", { static: false }) pagingTemplate: TemplateRef<any>;

	@Input() disableRowComparator: (rowItem: unknown) => boolean = () => false;

	register(property: string, template: TableCellTemplate) {
		this.templateMap.set(property, template);

		if (template.footer) {
			this.hasFooter = true;
		}
	}

	registerAs(templateName: string, template: TableCellTemplate) {
		this[templateName] = template;
	}

	unhook(templateName: string) {
		this[templateName] = undefined;
		this.defineAllColumns();
	}

	sort(key: string, direction: SortDirection) {
		if (!this.sortMap || !this.sortMap[key]) {
			return;
		}

		this.tableItems = [...this.items].sort((a, b) => {
			const result = this.sortMap[key](a[key], b[key]);

			return direction === SortDirection.DESCENDING ? -result : result;
		});
	}

	private defineAllColumns() {
		this.columns = [
			...(this.borderIndicationTemplate ? ["borderIndicationColumn"] : []),
			...(this.expandTemplate ? ["expandColumn"] : []),
			...(this.selectTemplate ? ["selectColumn"] : []),
			...(this.iconTemplate ? ["iconColumn"] : []),
			...this.tableColumns,
			...(this.actionsTemplate ? ["actionsColumn"] : []),
		];
	}

	private handleTemplateChanges() {
		// After each content check check if one of the below templates has been added or removed to rerender the columns
		let changes = false;

		if (this.templateMap.get("borderIndicationColumn") && !this.borderIndicationTemplate) {
			this.templateMap.delete("borderIndicationColumn");
			changes = true;
		}

		if (this.templateMap.get("expandColumn") && !this.expandTemplate) {
			this.templateMap.delete("expandColumn");
			changes = true;
		}

		if (this.templateMap.get("selectColumn") && !this.selectTemplate) {
			this.templateMap.delete("selectColumn");
			changes = true;
		}

		if (this.templateMap.get("iconColumn") && !this.iconTemplate) {
			this.templateMap.delete("iconColumn");
			changes = true;
		}

		if (this.templateMap.get("actionsColumn") && !this.actionsTemplate) {
			this.templateMap.delete("actionsColumn");
			changes = true;
		}

		if (this.selectTemplate && !this.templateMap.get("selectColumn")) {
			this.templateMap.set("selectColumn", this.selectTemplate);
			changes = true;
		}

		if (this.iconTemplate && !this.templateMap.get("iconColumn")) {
			this.templateMap.set("iconColumn", this.iconTemplate);
			changes = true;
		}

		if (this.actionsTemplate && !this.templateMap.get("actionsColumn")) {
			this.templateMap.set("actionsColumn", this.actionsTemplate);
			changes = true;
		}

		if (changes) {
			this.defineAllColumns();
		}
	}

	ngAfterContentInit() {
		this.defineAllColumns();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.items) {
			this.tableItems = this.items;
			this.empty = this.items && this.items.length === 0;
		}

		if (changes.items || changes.visible) {
			if (this.items && this.items.length > 0 && this.visible.length === 0) {
				this.tableColumns = Object.keys(this.items[0]);
				// Ignore the first change because the columns are defined on afterContentInit anyway first
				if (!changes.items.firstChange) {
					this.defineAllColumns();
				}
			} else if (changes.visible && this.visible && this.visible.length > 0) {
				this.tableColumns = this.visible;

				// Ignore the first change because the columns are defined on afterContentInit anyway first
				if (!changes.visible.firstChange) {
					this.defineAllColumns();
				}
			}
		}
	}

	ngAfterContentChecked() {
		// After each content check check if one of the below templates has been added or removed to rerender the columns
		this.handleTemplateChanges();
	}
}
